import { FieldValues } from '../schema';

const deriveGpsTrackingMode = (config: FieldValues): string => {
  if (config.gps_enabled) return 'gps_enabled';
  if (Number(config.periodic_gps_period) > 0 && !config.gps_enabled) {
    return 'periodic_gps';
  }
  return 'gps_only_alerts';
};

const deriveWatcherrSounds = (config: FieldValues): string => {
  if (!config.watch_muted && config.panic_button_muted) {
    return 'mute_alert_sounds';
  }
  if (config.watch_muted) return 'mute_all_sounds';
  return 'all_sounds';
};

export const mapConfigToForm = (config: FieldValues): FieldValues => {
  if (!config) return {};
  return {
    ...config,
    gps_tracking_mode: deriveGpsTrackingMode(config),
    watcherr_sounds: deriveWatcherrSounds(config),
  };
};

// `gps_tracking_mode` expands into the raw GPS params. The non-default branches
// always set `tracking: 3`.
// `tracking` is deprecated since 1.5.4, keeping it in for backwards comp
const expandGpsTrackingMode = (form: FieldValues): FieldValues => {
  switch (form.gps_tracking_mode) {
    case 'gps_only_alerts':
      return { periodic_gps_period: 0, gps_enabled: false, tracking: 3 };
    case 'periodic_gps':
      return {
        periodic_gps_period: form.periodic_gps_period ?? 300,
        periodic_gps_accuracy: form.periodic_gps_accuracy,
        periodic_gps_timeout: form.periodic_gps_timeout,
        tracking: 3,
      };
    case 'gps_enabled':
      return { gps_enabled: true, tracking: 3 };
    default:
      return {};
  }
};

const expandWatcherrSounds = (form: FieldValues): FieldValues => {
  switch (form.watcherr_sounds) {
    case 'all_sounds':
      return { watch_muted: false, panic_button_muted: false };
    case 'mute_alert_sounds':
      return { watch_muted: false, panic_button_muted: true };
    case 'mute_all_sounds':
      return { watch_muted: true, panic_button_muted: true };
    default:
      return {};
  }
};

// Synthetic fields map to several raw config keys,
// A field counts as differing when any of its source keys differs.
export const SYNTHETIC_FIELD_SOURCE_KEYS: Record<string, string[]> = {
  gps_tracking_mode: ['gps_enabled', 'periodic_gps_period'],
  watcherr_sounds: ['watch_muted', 'panic_button_muted'],
};

// The config key(s) a form field maps to — itself, unless it's synthetic.
export const configKeysForField = (fieldId: string): string[] => SYNTHETIC_FIELD_SOURCE_KEYS[fieldId] ?? [fieldId];

export const mapFormToConfig = (form: FieldValues): FieldValues => {
  const out: FieldValues = {};
  for (const [key, val] of Object.entries(form)) {
    // Synthetic fields and the raw gps* params are rebuilt below, never passed
    // through verbatim.
    if (key === 'gps_tracking_mode' || key === 'watcherr_sounds') continue;
    if (key.includes('gps')) continue;
    out[key] = val;
  }
  return {
    ...out,
    ...expandGpsTrackingMode(form),
    ...expandWatcherrSounds(form),
  };
};
